import type { FetchResult } from "./opsPaperworkClientBase";
import { opsFetch, q } from "./opsPaperworkClientBase";

export type WorkplaceChatStatusPayload = {
  ok: true;
  enabled: boolean;
  vendor: "stream" | null;
  stream_api_key: string | null;
  org_channels: string[];
  bridge_enabled: boolean;
  bridge_channel_slugs: string[];
  bridge_muted_channel_ids: string[];
  fallback_reason: string | null;
  policy_version: string;
};

export type WorkplaceChatTokenPayload = {
  ok: true;
  vendor: "stream";
  token: string;
  expires_at: string;
  org_id: string;
  org_ids?: string[];
  user_id: string;
  trust_level: 1 | 2 | 3;
  policy_version: string;
  read_only_channel_ids?: string[];
  stream_api_key: string | null;
};

export async function getWorkplaceChatStatus(): Promise<FetchResult<WorkplaceChatStatusPayload>> {
  return opsFetch(q("workplace-chat-status"), { method: "GET" });
}

export async function getWorkplaceChatToken(): Promise<FetchResult<WorkplaceChatTokenPayload>> {
  return opsFetch(q("workplace-chat-token"), { method: "GET" });
}

export async function createWorkplaceChatDm(recipientUserIds: string[]): Promise<
  FetchResult<{ ok: true; vendorChannelId: string; localKey: string; created: boolean }>
> {
  return opsFetch(q("workplace-chat-dm"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recipient_user_ids: recipientUserIds }),
  });
}

export async function createWorkplaceChatOrgChannel(input: {
  slug: string;
  display_name?: string;
  member_user_ids?: string[];
}): Promise<
  FetchResult<{
    ok: true;
    slug: string;
    display_name: string;
    vendor_channel_id: string;
    org_channels: string[];
    member_user_ids?: string[];
  }>
> {
  return opsFetch(q("workplace-chat-org-channel"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export async function getWorkplaceChatOrgChannelMembers(slug: string): Promise<
  FetchResult<{ ok: true; slug: string; vendor_channel_id: string; member_user_ids: string[] }>
> {
  return opsFetch(`${q("workplace-chat-org-channel")}&slug=${encodeURIComponent(slug)}`, {
    method: "GET",
  });
}

export async function patchWorkplaceChatOrgChannelMembers(input: {
  slug: string;
  member_user_ids: string[];
}): Promise<
  FetchResult<{ ok: true; slug: string; vendor_channel_id: string; member_user_ids: string[] }>
> {
  return opsFetch(q("workplace-chat-org-channel"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export async function deleteWorkplaceChatOrgChannel(slug: string): Promise<
  FetchResult<{ ok: true; deleted_slug: string; vendor_channel_id: string; org_channels: string[] }>
> {
  return opsFetch(`${q("workplace-chat-org-channel")}&slug=${encodeURIComponent(slug)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
}

export async function syncWorkplaceChatBridge(): Promise<
  FetchResult<{ ok: true; polled: number; notified: number; updated: number; throttled: number; synced_at: string }>
> {
  return opsFetch(q("workplace-chat-bridge-sync"), { method: "POST" });
}

export async function patchWorkplaceChatBridgeMute(input: {
  channel_id?: string;
  channel_slug?: string;
  muted: boolean;
}): Promise<FetchResult<{ ok: true; channel_id: string; muted: boolean; bridge_muted_channel_ids: string[] }>> {
  return opsFetch(q("workplace-chat-bridge-mute"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}
